import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Select,
  DatePicker,
  Input,
  Modal,
  Form,
  message,
  Calendar,
  Badge,
  Tooltip,
  Dropdown,
  Menu,
  Empty,
  Spin,
  Typography,
  Avatar,
} from "antd";
import {
  CalendarOutlined,
  UnorderedListOutlined,
  FilterOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  DollarOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import useMeetingStore from "../store/meetingStore";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const MeetingsPage = () => {
  const {
    meetings,
    stats,
    loading,
    error,
    pagination,
    filters,
    fetchMeetings,
    fetchMeetingStats,
    updateMeetingStatus,
    setPage,
    setLimit,
    applyFilters,
    clearFilters,
  } = useMeetingStore();

  const [viewMode, setViewMode] = useState("list");
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusForm] = Form.useForm();

  useEffect(() => {
    fetchMeetings();
    fetchMeetingStats();
  }, []);

  const handleStatusUpdate = async (values) => {
    try {
      await updateMeetingStatus(
        selectedMeeting.id,
        values.status,
        values.notes
      );
      message.success("Meeting status updated successfully");
      setStatusModalVisible(false);
      statusForm.resetFields();
      setSelectedMeeting(null);
      fetchMeetingStats(); // Refresh stats
    } catch (error) {
      message.error("Failed to update meeting status");
    }
  };

  const openStatusModal = (meeting) => {
    setSelectedMeeting(meeting);
    statusForm.setFieldsValue({
      status: meeting.status,
      notes: meeting.notes || "",
    });
    setStatusModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "blue";
      case "completed":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const getSourceIcon = (sourceType) => {
    return sourceType === "lead" ? <ContactsOutlined /> : <DollarOutlined />;
  };

  const columns = [
    {
      title: "Meeting",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <div>
          <div className="font-medium">{title}</div>
          <div className="text-sm text-gray-500 flex items-center space-x-2">
            {getSourceIcon(record.sourceType)}
            <span>{record.sourceName}</span>
            {record.sourceCompany && <span>• {record.sourceCompany}</span>}
          </div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "sourceType",
      key: "type",
      render: (type) => (
        <Tag color="blue">{type?.replace("_", " ").toUpperCase()}</Tag>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "scheduledAt",
      key: "scheduledAt",
      render: (date) => (
        <div>
          <div>{moment(date).format("MMM DD, YYYY")}</div>
          <div className="text-sm text-gray-500">
            {moment(date).format("hh:mm A")}
          </div>
        </div>
      ),
      sorter: (a, b) => moment(a.scheduledAt) - moment(b.scheduledAt),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (duration) => `${duration} min`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
      ),
      filters: [
        { text: "Scheduled", value: "scheduled" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (assignedTo) => (
        <div className="flex items-center space-x-2">
          <Avatar size="small" icon={<UserOutlined />} />
          <span>
            {assignedTo
              ? `${assignedTo.firstName} ${assignedTo.lastName}`
              : "Unassigned"}
          </span>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => openStatusModal(record)}
            icon={<ClockCircleOutlined />}
          >
            Update Status
          </Button>
        </Space>
      ),
    },
  ];

  const getCalendarData = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const dayMeetings = meetings.filter(
      (meeting) => moment(meeting.scheduledAt).format("YYYY-MM-DD") === dateStr
    );

    return dayMeetings.map((meeting) => ({
      type: getStatusColor(meeting.status),
      content: meeting.title,
    }));
  };

  const dateCellRender = (value) => {
    const listData = getCalendarData(value);
    return (
      <ul className="events">
        {listData.map((item, index) => (
          <li key={index}>
            <Badge status={item.type} text={item.content} />
          </li>
        ))}
      </ul>
    );
  };

  const handleFilterChange = (key, value) => {
    applyFilters({ [key]: value });
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      applyFilters({
        startDate: dates[0].toISOString(),
        endDate: dates[1].toISOString(),
      });
    } else {
      applyFilters({
        startDate: null,
        endDate: null,
      });
    }
  };

  const handleTableChange = (paginationInfo, filtersInfo, sorter) => {
    const params = {
      page: paginationInfo.current,
      limit: paginationInfo.pageSize,
    };

    // Handle table column filters
    if (filtersInfo.status && filtersInfo.status.length > 0) {
      params.status = filtersInfo.status[0];
    }

    // Handle sorting
    if (sorter.field) {
      params.sort = sorter.field;
      params.order = sorter.order === "ascend" ? "asc" : "desc";
    }

    fetchMeetings(params);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={2} className="mb-2">
            Meetings
          </Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Manage all your meetings from leads and deals
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchMeetings();
              fetchMeetingStats();
            }}
            loading={loading}
          >
            Refresh
          </Button>
          <Space.Compact>
            <Button
              type={viewMode === "list" ? "primary" : "default"}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode("list")}
            >
              List
            </Button>
            <Button
              type={viewMode === "calendar" ? "primary" : "default"}
              icon={<CalendarOutlined />}
              onClick={() => setViewMode("calendar")}
            >
              Calendar
            </Button>
          </Space.Compact>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Total Meetings"
              value={stats?.totalMeetings || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="This Week"
              value={stats?.totalThisWeek || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Completed"
              value={stats?.completed || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="shadow-soft">
            <Statistic
              title="Scheduled"
              value={stats?.scheduled || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-soft">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div>
              <Text strong>Date Range:</Text>
              <RangePicker
                style={{ width: "100%", marginTop: 4 }}
                onChange={handleDateRangeChange}
                value={
                  filters.startDate && filters.endDate
                    ? [moment(filters.startDate), moment(filters.endDate)]
                    : null
                }
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div>
              <Text strong>Type:</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={filters.type}
                onChange={(value) => handleFilterChange("type", value)}
              >
                <Option value="all">All</Option>
                <Option value="lead">Lead Meetings</Option>
                <Option value="deal">Deal Meetings</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div>
              <Text strong>Status:</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                allowClear
                placeholder="Status"
              >
                <Option value="scheduled">Scheduled</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                clearFilters();
                fetchMeetings();
              }}
              style={{ marginTop: 20 }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Content */}
      {viewMode === "list" ? (
        <Card className="shadow-soft">
          <Table
            columns={columns}
            dataSource={meetings}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} meetings`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </Card>
      ) : (
        <Card className="shadow-soft">
          <Calendar
            dateCellRender={dateCellRender}
            onSelect={(date) => {
              const dayMeetings = meetings.filter(
                (meeting) =>
                  moment(meeting.scheduledAt).format("YYYY-MM-DD") ===
                  date.format("YYYY-MM-DD")
              );
              if (dayMeetings.length > 0) {
                Modal.info({
                  title: `Meetings on ${date.format("MMMM DD, YYYY")}`,
                  content: (
                    <div className="space-y-3">
                      {dayMeetings.map((meeting) => (
                        <div
                          key={meeting.id}
                          className="border-l-4 border-blue-500 pl-3"
                        >
                          <div className="font-medium">{meeting.title}</div>
                          <div className="text-sm text-gray-500">
                            {moment(meeting.scheduledAt).format("hh:mm A")} •{" "}
                            {meeting.duration} min
                          </div>
                          <div className="text-sm">
                            <Tag color={getStatusColor(meeting.status)}>
                              {meeting.status?.toUpperCase()}
                            </Tag>
                            <span className="ml-2">{meeting.sourceName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                  width: 600,
                });
              }
            }}
          />
        </Card>
      )}

      {/* Status Update Modal */}
      <Modal
        title="Update Meeting Status"
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          statusForm.resetFields();
          setSelectedMeeting(null);
        }}
        footer={null}
        width={500}
      >
        {selectedMeeting && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="font-medium">{selectedMeeting.title}</div>
            <div className="text-sm text-gray-500 mt-1">
              {moment(selectedMeeting.scheduledAt).format(
                "MMM DD, YYYY hh:mm A"
              )}
            </div>
            <div className="text-sm text-gray-500">
              {selectedMeeting.sourceName} • {selectedMeeting.sourceCompany}
            </div>
          </div>
        )}

        <Form form={statusForm} layout="vertical" onFinish={handleStatusUpdate}>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select>
              <Option value="scheduled">Scheduled</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea
              rows={4}
              placeholder="Add any notes about this meeting..."
            />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setStatusModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MeetingsPage;
